// src/pages/TrainingArchive.js
// Coach/admin view: pick a student, see their full training history —
// every Square Training round (attempted/correct/wrong/accuracy) and every
// Writing Practice session — plus their linked chess.com / lichess rating.
import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc, deleteDoc } from "firebase/firestore";
import "./TrainingArchive.css";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function TrainingArchive({ students }) {
  const [selected, setSelected] = useState(null);      // student object
  const [results, setResults] = useState(null);        // null = loading
  const [chessAccount, setChessAccount] = useState(null);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setResults(null);
    setChessAccount(null);

    // Query by studentId only, sort client-side — no composite index needed.
    getDocs(query(collection(db, "trainingResults"), where("studentId", "==", selected.id)))
      .then(snap => {
        if (cancelled) return;
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
        setResults(rows);
      })
      .catch(err => {
        console.error("Could not load training results:", err);
        if (!cancelled) setResults([]);
      });

    getDoc(doc(db, "chessAccounts", selected.id))
      .then(snap => { if (!cancelled && snap.exists()) setChessAccount(snap.data()); })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [selected]);

  const squareRows = (results || []).filter(r => r.type === "square");
  const writingRows = (results || []).filter(r => r.type === "writing");

  async function deleteEntry(id) {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, "trainingResults", id));
      setResults(prev => (prev || []).filter(r => r.id !== id));
    } catch (err) {
      console.error("Could not delete entry:", err);
      alert("Couldn't delete — please try again.");
    }
  }

  const bestAccuracy = squareRows.reduce((best, r) => {
    const acc = r.attempted ? Math.round((r.correct / r.attempted) * 100) : 0;
    return Math.max(best, acc);
  }, 0);
  const totalPositions = writingRows.reduce((sum, r) => sum + (r.attempted || 0), 0);

  const ratingNow = chessAccount?.history?.length
    ? chessAccount.history[chessAccount.history.length - 1].r : null;
  const ratingFirst = chessAccount?.history?.length ? chessAccount.history[0].r : null;
  const ratingDelta = ratingNow !== null && ratingFirst !== null ? ratingNow - ratingFirst : null;

  return (
    <div className="training-archive">
      <div className="page-header">
        <div>
          <h2>🏋️ Training Archive</h2>
          <p className="page-sub">Every student's Square Training and Writing Practice history</p>
        </div>
      </div>

      <div className="ta-body">
        {/* student picker */}
        <div className="ta-students">
          {students.map(s => (
            <button key={s.id}
              className={`ta-student-btn ${selected?.id === s.id ? "active" : ""}`}
              onClick={() => setSelected(s)}>
              <span className="ta-student-avatar">{(s.name || "?")[0].toUpperCase()}</span>
              {s.name}
            </button>
          ))}
          {students.length === 0 && <p style={{ color: "var(--text-muted)", fontSize: 13 }}>No students yet.</p>}
        </div>

        {/* results */}
        <div className="ta-results">
          {!selected && (
            <div className="empty-state"><div>👈</div><p>Pick a student to see their training history</p></div>
          )}

          {selected && results === null && (
            <div className="loading-screen" style={{ minHeight: 160 }}><div className="spinner" /></div>
          )}

          {selected && results !== null && (
            <>
              <div className="ta-summary">
                <div className="ta-sum-card">
                  <span className="ta-sum-num">{squareRows.length}</span>
                  <span className="ta-sum-label">square rounds</span>
                </div>
                <div className="ta-sum-card">
                  <span className="ta-sum-num">{squareRows.length ? `${bestAccuracy}%` : "—"}</span>
                  <span className="ta-sum-label">best accuracy</span>
                </div>
                <div className="ta-sum-card">
                  <span className="ta-sum-num">{totalPositions}</span>
                  <span className="ta-sum-label">notation positions</span>
                </div>
                <div className="ta-sum-card">
                  <span className="ta-sum-num">
                    {ratingNow !== null ? ratingNow : "—"}
                    {ratingDelta !== null && ratingDelta !== 0 && (
                      <em className={ratingDelta > 0 ? "up" : "down"}> {ratingDelta > 0 ? `▲+${ratingDelta}` : `▼${ratingDelta}`}</em>
                    )}
                  </span>
                  <span className="ta-sum-label">
                    {chessAccount ? `${chessAccount.platform} rating` : "no linked account"}
                  </span>
                </div>
              </div>

              <h3 className="ta-section-h">🎯 Square Training</h3>
              {squareRows.length === 0 && <p className="ta-empty-line">No rounds played yet.</p>}
              {squareRows.length > 0 && (
                <table className="ta-table">
                  <thead>
                    <tr><th>When</th><th>Timer</th><th>Attempted</th><th>Correct</th><th>Wrong</th><th>Accuracy</th><th></th></tr>
                  </thead>
                  <tbody>
                    {squareRows.map(r => {
                      const acc = r.attempted ? Math.round((r.correct / r.attempted) * 100) : 0;
                      return (
                        <tr key={r.id}>
                          <td>{fmtDate(r.createdAt)}</td>
                          <td>{r.duration}s</td>
                          <td>{r.attempted}</td>
                          <td className="good">{r.correct}</td>
                          <td className="bad">{r.wrong}</td>
                          <td className={acc >= 80 ? "good" : acc < 50 ? "bad" : ""}>{acc}%</td>
                          <td><button className="ta-del" title="Delete entry" onClick={() => deleteEntry(r.id)}>🗑</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              <h3 className="ta-section-h">✍️ Writing Practice</h3>
              {writingRows.length === 0 && <p className="ta-empty-line">No sessions yet.</p>}
              {writingRows.length > 0 && (
                <table className="ta-table">
                  <thead>
                    <tr><th>When</th><th>Positions practiced</th><th></th></tr>
                  </thead>
                  <tbody>
                    {writingRows.map(r => (
                      <tr key={r.id}>
                        <td>{fmtDate(r.createdAt)}</td>
                        <td>{r.attempted}</td>
                        <td><button className="ta-del" title="Delete entry" onClick={() => deleteEntry(r.id)}>🗑</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
